/**
 * Date Utility Functions for RIS Dashboard
 * Enforces Indian Standard Time (IST) across the application.
 */

/**
 * Get current time in IST as an ISO string.
 * This simulates the backend get_ist_now().
 */
export const getISTNowISO = () => {
    // Generate the time in IST
    const now = new Date();
    // Use Intl to format the parts in IST, then construct the ISO
    return new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })).toISOString();
};

/**
 * Get a local Date object representing the time in IST.
 */
export const getISTDate = (dateStr = null) => {
    const d = dateStr ? new Date(dateStr) : new Date();
    // Convert the exact moment 'd' to what the clock reads in Asia/Kolkata
    // Since JavaScript Date objects are fundamentally tied to the browser's local timezone, 
    // a common trick is to format to a string in the target timezone and parse it back 
    // to "trick" the JS Date object into holding those exact year/month/day/hour/minute values.
    const istString = d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
    return new Date(istString);
};

/**
 * Returns the difference in days between today (in IST) and the target date (in IST).
 * Positive = Future, Negative = Past
 * @param {string} dateStr - ISO date string or YYYY-MM-DD
 * @returns {number} Difference in days
 */
export const getDaysDifference = (dateStr) => {
    if (!dateStr) return null;

    // Get "today" at midnight in IST
    const nowIst = getISTDate();
    nowIst.setHours(0, 0, 0, 0);

    // Get "target date" at midnight in IST
    const targetIst = getISTDate(dateStr);
    targetIst.setHours(0, 0, 0, 0);

    const diffTime = targetIst - nowIst;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
};

/**
 * Formats a date string into a human-readable "Due in X days" format.
 * @param {string} dateStr 
 * @returns {string} e.g., "Due Today", "Due in 5 days", "Overdue by 2 days"
 */
export const formatDueDate = (dateStr) => {
    const days = getDaysDifference(dateStr);

    if (days === null) return 'No Due Date';
    if (days === 0) return 'Due Today';
    if (days === 1) return 'Due Tomorrow';
    if (days === -1) return 'Overdue by 1 day';

    if (days > 0) return `Due in ${days} days`;
    return `Overdue by ${Math.abs(days)} days`;
};

/**
 * Formats a date string to "DD MMM YYYY" (e.g., 12 Feb 2026) in IST.
 * @param {string} dateStr 
 * @returns {string}
 */
export const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'Asia/Kolkata'
    });
};

/**
 * Formats a date string to "DD MMM YYYY HH:MM AM/PM" in IST.
 */
export const formatDateTimeIST = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Kolkata'
    });
};

/**
 * Checks if a date is within the next X days (inclusive of today).
 * @param {string} dateStr 
 * @param {number} days 
 * @returns {boolean}
 */
export const isDueUpcoming = (dateStr, days) => {
    const diff = getDaysDifference(dateStr);
    return diff !== null && diff >= 0 && diff <= days;
};
